<div id="top" class="policy-wrap">
  <h1>Pets at Home Privacy Policy</h1>
  <p class="subtitle">
    <strong>Last updated: </strong>
    <time datetime="2026-11-01">01 November 2026</time>
  </p>

  <h2>1. Introduction</h2>
  <p>
    The Pets at Home Family respects your right to privacy. This Privacy Policy
    explains in detail the types of personal information we may collect about
    you when you interact with us. It also explains how we will store and handle
    that personal information, how we keep it safe, how you can exercise your
    privacy rights and how we combine data across the Pets at Home Family which
    enables us to understand you and your pet better to ultimately improve your
    experience with the Pets at Home Family.
  </p>
  <p>
    If you have any questions or concerns about our use of your personal
    information, then please contact us using the contact details provided in
    the section of this Privacy Policy headed "How to contact us"
    <a href="/privacy-policy#how-to-contact-us">below</a>.
  </p>

  <h2>2. What is the Pets at Home Family?</h2>
  <p>
    Pets at Home is the UK's leading pet specialist, providing pet owners with
    all their needs; food, accessories, vet and grooming services. Pets at Home
    Limited is comprised of our retail stores, retail website (petsathome.com)
    <strong>(the “Website”)</strong> and the Pets at Home app
    <strong>(the “App”)</strong>, Pets Grooming and Pets Club
    <strong>(“Pets at Home”)</strong>. Pets at Home Group Plc is the ultimate
    parent company of Pets at Home.
  </p>
  <p>
    References to the “Pets at Home Family” means the Pets at Home group
    companies and affiliated joint venture entities, which currently includes:
  </p>
  <ul>
    <li>
      <strong>Pets at Home Group plc</strong> (the ultimate parent company of
      Pets at Home);
    </li>
    <li>
      <strong>Pets at Home Limited</strong> (Pets at Home, which includes our
      retail stores, the website and apps, Pets Grooming, and Pets Club, our
      loyalty club);
    </li>
    <li>
      <strong>Pets Insurance Services Limited (Pets Insurance)</strong>, a
      company which administers pet insurance policies;
    </li>
    <li>
      <strong>Pets at Home Vet Group Limited</strong> (comprising Companion Care
      (Services) Limited, Companion Care Management Services Limited, Vets4Pets
      Limited and our affiliated joint venture first opinion veterinary
      practices branded either Companion Care, Vets for Pets or Vets4Pets);
    </li>
    <li>
      <strong>Vetsdirect Limited</strong> (trading as
      <strong>The Vet Connection</strong>); and
    </li>
    <li><strong>Pets Foundation</strong> our affiliated charity.</li>
  </ul>
  <p>together <strong>“Pets at Home Family”.</strong></p>
  <p>
    This Privacy Policy focuses on the activities of
    <strong>Pets at Home Group plc, Pets at Home and Pets Insurance.</strong>
  </p>
  <p>
    For more information about the activities of our other Pets at Home Family
    members, please visit their <strong>respective privacy policies</strong> as
    follows:
  </p>
  <ul>
    <li>
      <a
        href="https://www.vets4pets.com/about-us/privacy-policy"
        target="_blank"
        rel="noreferrer"
        ><strong>Pets at Home Vet Group Limited</strong></a
      >
    </li>
    <li>
      <a
        href="https://www.thevetconnection.co.uk/privacy-policy/"
        target="_blank"
        rel="noreferrer"
        ><strong>Vetsdirect Limited (The Vet Connection)</strong></a
      >
    </li>
    <li>
      <a
        href="https://www.petsfoundation.co.uk/privacy-policy/"
        target="_blank"
        rel="noreferrer"
        ><strong>Pets Foundation</strong></a
      >
    </li>
  </ul>
  <p>
    The terms "we", "us" or "our" in this Privacy Policy refer to the Pets at
    Home Family. The terms "Website" and "App" refer to our websites
    (petsathome.com petsinsurance.co.uk and vets4pets.com) and the Pets at Home
    (Pets) app which are owned and operated by the Pets at Home Family.
  </p>
  <h2>3. Quick Links</h2>
  <p>
    We recommend that you read this Privacy Policy in full to ensure you are
    fully informed. However, if you only want to access a particular section of
    this Privacy Policy, then you can click on the relevant link below to jump
    to that section.
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#when-personal"
        >When do we collect personal information?</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#what-personal"
        >What personal information do we collect and why?</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#who-personal"
        >Who do we share your personal information with?</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#legal-basis-personal"
        >Legal Basis for processing personal information</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#cookies-tracking"
        >Cookies and similar tracking technology</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#how-personal"
        >How do we keep your personal information secure?</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#international-data"
        >International Data Transfer</a
      ></strong
    >
  </p>
  <p>
    <strong><a href="/privacy-policy#data-retention">Data Retention</a></strong>
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#dpr-rights"
        >Your data protection rights</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#contacting-the-regulator"
        >Contacting the regulator</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#policy-updates"
        >Updates to this Privacy Policy</a
      ></strong
    >
  </p>
  <p>
    <strong
      ><a href="/privacy-policy#how-to-contact-us">How to contact us</a></strong
    >
  </p>

  <a id="when-personal"></a>
  <h2>4. When do we collect personal information?</h2>
  <p>
    We collect your personal information in a number of instances, including
    when you:
  </p>
  <p>
    <em
      >engage with Pets at Home (including Pets Grooming and/or Pets Club)
      by;</em
    >
  </p>
  <ul>
    <li>creating a Pets Club account to shop with us;</li>
    <li>using your Pets Club account;</li>
    <li>
      creating and/or managing an Easy Repeat plan or Health Plan with us;
    </li>
    <li>
      making an online purchase and checking out as a guest (in which case we
      collect transaction-based data);
    </li>
    <li>
      booking any kind of appointment with us or booking to attend an event, for
      example Grooming, Nutrition Consultations, Health Plan Consultations,
      Puppy Classes or My Pet Pals Workshops;
    </li>
    <li>
      visiting our Website or App, and using your account to buying products and
      services or redeeming vouchers from us on the phone, in a shop or online;
    </li>
    <li>
      purchasing a product or service in store or by phone but don't have (or
      don't use) a Pets Club account;
    </li>
    <li>
      engaging with us or one of our ads on social media or whilst browsing
      online or contacting us by any means, including where you ask one of our
      colleagues to email you about information about a product or service or
      where you telephone us or we telephone you (where we may record phone
      conversations for record or training purposes);
    </li>
    <li>engaging with our App;</li>
    <li>using our in-store WIFI;</li>
    <li>buying a pet from us;</li>
    <li>
      entering into a prize draw, raffle or competition hosted/promoted by us;
    </li>
    <li>
      writing and/or publishing consumer reviews about our products and services
      (please note that any individual may access personal information related
      to them, including opinions. So, if your comment or review includes
      information about the colleague who provided that service, it may be
      passed on to them if requested);
    </li>
    <li>
      filling in any forms (e.g. if an accident happens in store, a colleague
      may collect your personal information);
    </li>
    <li>
      having given a third party permission to share the personal information
      they hold about you with us;
    </li>
    <li>
      Use our car parks or shops, which usually have CCTV systems (including
      body-worn cameras where it is deemed necessary) operated for the safety of
      both customers and colleagues and for the prevention of crime. These
      systems may record your image during your visit.
    </li>
  </ul>
  <p><em>engage with Pets Insurance by;</em></p>
  <ul>
    <li>obtaining a quote and/or purchasing a policy with Pets Insurance;</li>
    <li>
      making enquiries through a price comparison website or an authorised third
      party for pet insurance with Pets Insurance;
    </li>
    <li>registering a pet insurance claim with Pets Insurance; and</li>
    <li>creating a Pets Club account to manage your Pets Insurance policy.</li>
  </ul>
  <p>
    We also collect your personal information when our suppliers and partners
    share information with us about the product or service you have purchased.
  </p>

  <a id="what-personal"></a>
  <h2>5. What personal information do we collect and why?</h2>
  <p>
    The personal information that we collect about you broadly falls into the
    following categories:
  </p>

  <h3>(a) Information that you provide voluntarily</h3>
  <p><em>Basic personal and contact information</em></p>
  <p>
    When you make a purchase in store or online, create a Pets Club account,
    book an appointment, attend an event in store, set up a Pets at Home Easy
    Repeat or Health Plan, book a Pets Grooming appointment, apply for pet
    insurance with Pets Insurance or download our App or enter a prize
    draw/competition, you may be asked for some essential information including
    your title, first name, surname, postal address, email address, telephone,
    details of your pet and, if applicable, payment information. These are
    collected to help us:
  </p>
  <ul>
    <li>
      Process your online orders including sending order confirmation and
      delivery information via email or text message, or process payments due to
      us (including for treatments for your pet);
    </li>
    <li>
      Contact you about your pet, your order, account or credit status where
      required;
    </li>
    <li>
      Deliver safe and secure shopping by helping to prevent and detect fraud –
      to do this, we may need to disclose information to assist legal or debt
      recovery processes;
    </li>
    <li>
      Understand your needs and listen to your feedback via market
      research/shopping analysis, customer polls and surveys;
    </li>
    <li>
      Register your pet with our easy-repeat service or Health Plan service;
    </li>
    <li>Manage and process easy-repeat plan or Health Plan;</li>
    <li>
      Enhance your Pets at Home and Pets at Home Family experience by delivering
      personally tailored retail offers;
    </li>
    <li>
      Send you marketing and promotional offers from the Pets at Home Family and
      to manage your marketing preferences with your agreement;
    </li>
    <li>
      To administer your Pets Club account and to contact you with offers and
      promotions from our Pets Club (including from other members of the Pets at
      Home Family and selected third party partners);
    </li>
    <li>
      To administer your Pets Insurance Policy via your Pets Club account;
    </li>
    <li>Speed up your form filling processes if you've registered online;</li>
    <li>
      Provide you with information about the fundraising activities of our
      affiliated charity, Pets Foundation;
    </li>
    <li>
      Respond to any issues or queries via our customer service adviser teams;
    </li>
    <li>
      Conduct analytics to understand how our products and services (and the
      products and services of our Pets at Home Family members) are used and to
      improve your customer experience; and/or
    </li>
    <li>Notify you about changes to our service.</li>
  </ul>

  <p><em>Additional information</em></p>
  <p>
    Where you are engaging with Pets Insurance, we may also ask for additional
    information to enable us to assess, manage and otherwise administer your
    insurance policy such as:
  </p>
  <ul>
    <li>
      Information required to make decisions about your application for pet
      insurance with Pets Insurance;
    </li>
    <li>
      Additional personal information such as date of birth and other relevant
      information to enable us to assess your application for pet insurance; and
    </li>
    <li>Details about claims history in relation to your pet.</li>
  </ul>
  <p>
    When you use our Website or App as an account holder or guest user, we also
    collect your email address and may contact you to let you know if you
    abandon a purchase part way through the process and have unpurchased items
    in your basket.
  </p>
  <p>
    Where you have made a donation to our affiliated charity,
    <a href="https://www.petsathome.com/support-adoption-for-pets"
      >The Pets at Home Foundation</a
    >, via the Just Giving, Virgin Money website or via Paypal and agree with
    Just Giving, Virgin Money or Paypal that they can pass your information to
    us (to use to send you thank you emails and news about our fundraising
    activities), this information will be processed in accordance with the
    <a href="https://www.petsathome.com/support-adoption-for-pets"
      >Pets at Home Foundation</a
    >
    Privacy Policy which can be found
    <a
      href="https://www.supportadoptionforpets.co.uk/privacy-policy"
      target="_blank"
      rel="noreferrer"
      >here</a
    >.
  </p>

  <h3>(b) Information that we collect automatically</h3>
  <p>
    When you visit our Website or App or use our in-store WIFI, we may collect
    certain information automatically from your device. This information may be
    considered to be your personal information under applicable data protection
    laws.
  </p>
  <p>
    Specifically, the information we collect automatically may include
    information like your IP address, device type, operating system, unique
    device identification numbers, browser-type, broad geographic location (e.g.
    country or city-level location) and other technical information. We may also
    collect information about how your device has interacted with our Website,
    App and in-store WIFI, including the pages accessed and links clicked.
  </p>
  <p>
    Collecting this information enables us to better understand the visitors who
    come to and use our Website, App and in-store WIFI. We may look at where
    they come from, what content is of interest to them, what they have viewed
    or looked at and in some instances what they have purchased. We use this
    information for internal analytics purposes, to improve the quality and
    relevance of our Website, App and in-store WIFI to our visitors and users
    and to deliver personally tailored retail offers (both on the website and
    through triggered email, SMS and direct mail (but only where you have agreed
    to these channels)).
  </p>
  <p>
    Some of this information may be collected using cookies, tracking pixels and
    similar tracking technology, as explained further under the heading "Cookies
    and similar tracking technology"
    <a href="/privacy-policy#cookies-tracking">below</a>.
  </p>

  <h3>(c) Information that we obtain from third party sources</h3>
  <p>
    From time to time, we may receive personal information about you from third
    party sources, but only where we have checked that these third parties
    either have your consent or are otherwise legally permitted or required to
    disclose your personal information to us.
  </p>
  <p>
    Additionally, if you are an existing or prospective Pets Insurance customer
    who has engaged with Pets Insurance by requesting a quote or purchasing a
    policy, we may receive information from other sources such as sanctions
    lists and data suppliers such as Experian, and publicly available
    information under the Open Government Licence.
  </p>
  <p>
    If you decide you wish to save your payment card details for future use when
    shopping with us, you will be able to do so provided you tick the ‘save card
    for future use’ box when you input your details during a transaction on the
    Website or App. Please note if your saved payment card expires, or is lost,
    stolen or damaged, your bank will update us with your new card details, and
    we will update your saved payment card in your account. You can remove your
    saved payment card in My Account by managing your payment methods under the
    My Details section at any time.
  </p>
  <p>
    If you have any questions about the third parties we obtain personal
    information from, please contact us using the contact details provided under
    the "How to contact us" heading
    <a href="/privacy-policy#how-to-contact-us">below</a>.
  </p>

  <h3>(d) Enhancing our data</h3>
  <p>
    Where we have obtained your personal information from various sources
    (including personal information we have lawfully obtained from third parties
    or from other members of the Pets at Home Family) we will combine this
    information in order to enhance our understanding of your requirements and
    preferences in relation to our products and services, and the products,
    services and charity activities of Pets at Home Family members. This is
    necessary for the purposes of our legitimate interests to understand our
    customers better, to personalise your Pets at Home Family experience, and
    where you have agreed to receive marketing to ensure that we provide you the
    most appropriate offers from us and about our Pets at Home Family products,
    services and charity activities.
  </p>
  <p>
    If you have more than one account (Pets Club or otherwise) registered with
    us, your accounts may be matched and merged to create one single record for
    you under one of those accounts. The other accounts you hold will be closed.
    If you have queries in relation to your account, contact our Customer
    Service team at <a href="/privacy-policy#how-to-contact-us">contact us</a>.
  </p>

  <h3>(e) Conducting analytics</h3>
  <p>
    We will use the personal information we hold about you (as well as
    pseudonymised or anonymised information generated from your personal
    information) to carry out analysis and research. We carry out all such
    analysis and research on the basis that it is necessary for the purposes of
    our legitimate interests in understanding our customers and ensuring that
    our products and services meet the needs of our customers. We undertake data
    analytics, whereby together with the Pets at Home Family, we combine
    information we hold on a large scale, in order to:
  </p>
  <ul>
    <li>learn more about our customers and their preferences;</li>
    <li>to identify patterns and trends amongst our customers;</li>
    <li>enhance user experience on our Websites and Apps;</li>
    <li>
      provide information, content, online advertising and offerings tailored to
      our customers' needs;
    </li>
    <li>for general research and statistical purposes;</li>
    <li>for aggregated reporting purposes within the Pets at Home Family;</li>
    <li>to help us develop new products and services;</li>
    <li>to monitor performance of our products and services;</li>
    <li>to be able to send you personalised marketing messages;</li>
    <li>to display online advertisements to you; and</li>
    <li>
      to measure or understand the effectiveness of our online advertising; and
    </li>
    <li>to provide accurate pricing for our insurance products.</li>
  </ul>
  <p>
    We will use your personal information (including by anonymising and
    aggregating it with other customers' personal information) for sales, supply
    chain and financial analysis purposes, to determine how we are performing
    and where improvements can be made and where necessary to report back to
    other members of the Pets at Home Family. This is necessary for the purposes
    of our legitimate interests in understanding how our business is performing,
    and considering how to improve our performance.
  </p>

  <h3>(f) Marketing</h3>
  <p>
    We always give you the choice of whether or not you wish to receive or
    continue receiving marketing communications from us. This includes marketing
    by phone, email, SMS and post.
  </p>
  <p>
    If you open a Pets Club account your marketing preferences for email and SMS
    contact will be collected on an opt-out basis, which will be made clear to
    you at the time of collecting your contact details and marketing preferences
    (unless you are opening a Pets Club Account as part of the Pets Insurance
    sign up journey in which case your marketing preferences for email and SMS
    contact will be collected on an opt-in basis). For marketing sent to you by
    phone and post we rely on legitimate interest as the lawful basis for
    processing your data and you can opt out of phone and postal marketing at
    any time by updating your preferences in My Account when signed in on our
    Website or App.
  </p>
  <p>
    By opening a Pets Club account, you consent to the Pets at Home Family
    contacting you from time to time by email, post, phone and SMS with details
    of personalised benefits including savings, offers, reminders and
    information about products and services from the Pets at Home family and our
    selected Pets Club partners unless you opt out of email and SMS during the
    sign up process.
  </p>
  <p>
    Please note that any important service messages will still be sent to you
    regardless of your marketing preferences.
  </p>
  <p>
    You can adjust your preferred marketing channels or opt-out at any time by
    visiting 'My Communication Preferences' in 'My Account' or by contacting us
    at contact us. You can also opt-out or withdraw your consent to email
    marketing by clicking 'Unsubscribe' on a marketing email from us. Please see
    "Your data protection rights"
    <a href="/privacy-policy#dpr-rights">below</a> for more information.
  </p>

  <p class="back-to-top"><a href="/privacy-policy#top">Back to top</a></p>

  <a id="who-personal"></a>
  <h2>6. Who do we share your personal information with?</h2>
  <p>
    We may disclose your personal information to the following categories of
    recipients:
  </p>
  <ul>
    <li>
      to other members of the
      <strong
        >Pets at Home Family, our third party service providers and business
        partners</strong
      >
      who provide data processing services to us, or who otherwise process
      personal information for purposes that are described in this Privacy
      Policy or notified to you when we collect your personal information.
      Examples of the kind of third parties we work with are:
    </li>
    <ul>
      <li>
        IT companies who support our Website, App and other business systems;
      </li>
      <li>our insurance underwriter;</li>
      <li>operational companies such as delivery couriers;</li>
      <li>
        direct marketing, loyalty reward companies or other third parties who
        help us manage electronic communications with you;
      </li>
      <li>
        Google, Facebook, Instagram, Twitter and similar platforms and partners
        to show you products and services that might interest you while you're
        browsing the internet; This is based on either your marketing consent or
        your acceptance of cookies, tracking pixels or similar tracking
        technology whilst browsing online, as explained further under the
        heading "Cookies and similar tracking technology"
        <a href="/privacy-policy#cookies-tracking">below</a>.
      </li>
      <li>
        data insight and data analysis companies (to provide us with tools to
        analyse the data which we hold);
      </li>
      <li>promotional partners (such as cashback or voucher sites);</li>
      <li>
        consumer review companies (so you can leave feedback and we can improve
        our service to you);
      </li>
      <li>competition or prize-draw partners and promoters;</li>
    </ul>
    <li>
      to a <strong>product supplier</strong> of ours when you purchase a product
      from us which is shipped directly from that product supplier to you, and
      their trusted courier. We will make it clear to you if this applies to
      your order during checkout.
    </li>
    <li>
      to any competent
      <strong
        >law enforcement body, regulatory, government agency, court or other
        third party</strong
      >
      where we believe disclosure is necessary (i) as a matter of applicable law
      or regulation, (ii) to exercise, establish or defend our legal rights or
      apply our
      <a href="https://www.petsathome.com/terms-and-conditions"
        >Terms and Conditions</a
      >, or (iii) to protect your vital interests or those of any other person;
    </li>
    <li>
      to a <strong>potential buyer </strong>(and its agents and advisers) in
      connection with any proposed purchase, merger or acquisition of any part
      of our business, provided that we inform the buyer it must use your
      personal information only for the purposes disclosed in this Privacy
      Policy; and/or
    </li>
    <li>
      to any
      <strong>other third party with your consent to the disclosure.</strong>
    </li>
  </ul>

  <p>
    Where we share your personal information with third party service providers
    and partners we apply a policy to ensure your personal information is safe
    and in order to protect your privacy. Our policy requires:
  </p>
  <ul>
    <li>
      We provide only the information they need to perform their specific
      services.
    </li>
    <li>
      They may only use your data for the purposes we specify in our contract
      with them.
    </li>
    <li>
      We work closely with them to ensure that your privacy is respected and
      protected at all times.
    </li>
    <li>
      If we stop using their services, any of your personal information held by
      them will either be deleted or rendered anonymous (subject to applicable
      law).
    </li>
  </ul>
  <p>
    If you have any questions about the third parties we share your personal
    information with, please contact us using the contact details provided under
    the "How to contact us" heading
    <a href="/privacy-policy#how-to-contact-us">below</a>.
  </p>
  <p>
    With your agreement, third parties (such as Google, Bing, Yahoo, Facebook,
    Instagram, X, Pinterest, Snapchat, TikTok) may serve targeting cookies on
    your computer or mobile device while browsing our Websites. These companies
    may use information about your visits to this and other websites in order to
    provide relevant advertisements about goods and services that you may be
    interested in. They may also employ technology that is used to measure the
    effectiveness of advertisements. This can be accomplished by them using
    cookies or web beacons to collect information about your visits to this and
    other sites in order to provide relevant advertisements about goods and
    services of potential interest to you. The information collected through
    this process does not enable us or them to identify your name, contact
    details or other personally identifying details unless you choose to provide
    these (based on your marketing consent or acceptance of cookies). Please see
    “Cookies and similar tracking technology”
    <a href="https://www.petsathome.com/privacy-policy#cookies-tracking"
      >below</a
    >
    for more information.
  </p>

  <a id="legal-basis-personal"></a>
  <h2>7. Legal basis for processing personal information</h2>
  <p>
    Our legal basis for collecting and using the personal information described
    above will depend on the personal information concerned and the specific
    context in which we collect it.
  </p>
  <p>
    However, we will normally collect personal information from you only (i)
    where we need the personal information to perform a contract with you, (ii)
    where the processing is in our legitimate interests and not overridden by
    your data protection interests or fundamental rights and freedoms, or (iii)
    where we have your consent to do so. In some cases, we may also have a legal
    obligation to collect personal information from you or may otherwise need
    the personal information to protect your vital interests or those of another
    person.
  </p>
  <p>
    If we ask you to provide personal information to comply with a legal
    requirement or to perform a contract with you, we will make this clear at
    the relevant time and advise you whether the provision of your personal
    information is mandatory or not (as well as of the possible consequences if
    you do not provide your personal information).
  </p>
  <p>
    Similarly, if we collect and use your personal information in reliance on
    our legitimate interests (or those of any third party), we will make clear
    to you at the relevant time what those legitimate interests are.
  </p>
  <p>
    If you have questions about or need further information concerning the legal
    basis on which we collect and use your personal information, please contact
    us using the contact details provided under the "How to contact us" heading
    <a href="/privacy-policy#how-to-contact-us">below</a>.
  </p>

  <a id="cookies-tracking"></a>
  <h2>8. Cookies and similar tracking technology</h2>
  <p>
    We use cookies and similar tracking technology (collectively,
    “<strong>Cookies</strong>”) to collect and use personal information about
    you, including serving interest-based advertising. For further information
    about the types of Cookies we use, why, and how you can control Cookies,
    please see our Cookie Notice
    <a href="https://www.petsathome.com/cookies">here</a>.
  </p>

  <a id="how-personal"></a>
  <h2>9. How do we keep your personal information secure?</h2>
  <p>
    We use appropriate technical and organisational measures to protect the
    personal information that we collect and process about you. The measures we
    use are designed to provide a level of security appropriate to the risk of
    processing your personal information. Specific measures we use include:
  </p>
  <ul>
    <li>
      Encrypting your personal information. When you place an order or access
      your account information, your information will be sent through to us. A
      secure server encrypts all of the information you input before it is sent
      to us. This protects your confidential data, particularly your payment
      card details from unauthorised use;
    </li>
    <li>
      Keeping your information up-to-date and accurate. Please note that, to do
      this, we require you to tell us if any of your details such as your name
      or address change; and
    </li>
    <li>
      Having in place strict security procedures for the storage and disclosure
      of your information to prevent unauthorised access.
    </li>
  </ul>
  <p>
    Unfortunately, the transmission of information via the Internet is not
    completely secure. Although we will do our best to protect your personal
    information, we cannot guarantee the security of your data transmitted to
    and/or through our Website and App; any transmission is at your own risk.
    Once we have received your information, we will use strict procedures and
    security features to try to prevent unauthorised access.
  </p>
  <p>
    To ensure that your credit, debit or charge card is not being used without
    your consent, we will validate name, address and other personal information
    supplied by you during the order process against appropriate third party
    databases. By accepting our
    <a href="https://www.petsathome.com/terms-and-conditions"
      >Terms and Conditions</a
    >
    you agree to these checks being made. In performing these checks personal
    information provided by you may be disclosed to a registered Credit
    Reference Agency which may keep a record of that information. You can rest
    assured that this is done only to confirm your identity, that a credit check
    is not performed and that your credit rating will be unaffected. All
    information provided by you will be treated securely and strictly in
    accordance with this Privacy Policy.
  </p>

  <a id="international-data"></a>
  <h2>10. International data transfers</h2>
  <p>
    Your personal information may be transferred to, and processed in, countries
    other than the country in which you are resident. These countries may have
    data protection laws that are different to the laws of the UK.
  </p>
  <p>
    Our Website servers and group companies are located in the UK (excluding our
    sourcing office which is based in Hong Kong), but some of our third party
    service providers and partners operate outside of the UK, such as in the EU,
    EEA, Australia and the United States. This means that when we collect your
    personal information we may process it in any of these countries.
  </p>
  <p>
    If we do this, we have procedures in place to ensure your personal
    information receives the same protection as if it were being processed
    inside the UK. For example, our contracts with third parties stipulate the
    standards they must follow at all times. If you wish for more information
    about these contracts please contact us using the contact details provided
    under the "How to contact us" heading
    <a href="/privacy-policy#how-to-contact-us">below</a>.
  </p>
  <p>
    Any transfer of your personal data will be compliant with UK data protection
    law and all personal data will be secure.
  </p>

  <a id="data-retention"></a>
  <h2>11. Data retention</h2>
  <p>
    We retain personal information we collect from you where we have an ongoing
    legitimate business need to do so (for example, to provide you with a
    service you have requested or to comply with applicable legal, tax or
    accounting requirements).
  </p>
  <p>
    When we have no ongoing legitimate business need to process your personal
    information, we will either delete or anonymise it or, if this is not
    possible (for example, because your personal information has been stored in
    backup archives), then we will securely store your personal information and
    isolate it from any further processing until deletion is possible. For more
    information about the specific periods for which we retain your data, please
    contact us using the contact details provided under the "How to contact us"
    heading <a href="/privacy-policy#how-to-contact-us">below</a>.
  </p>

  <a id="dpr-rights"></a>
  <h2>12. Your data protection rights</h2>
  <p>You have the following data protection rights:</p>
  <ul>
    <li>
      If you wish to
      <strong
        >access, correct, update or request deletion of your personal
        information</strong
      >, you can do so at any time by contacting us using the contact details
      provided under the "How to contact us" heading
      <a href="/privacy-policy#how-to-contact-us">below</a>.
    </li>
    <li>
      In addition, you can
      <strong
        >object to processing of your personal information, ask us to restrict
        processing</strong
      >
      of your personal information or <strong>request portability</strong> of
      your personal information. Again, you can exercise these rights by
      contacting us using the contact details provided under the "How to contact
      us" heading <a href="/privacy-policy#how-to-contact-us">below</a>.
    </li>
    <li>
      You have the right to
      <strong>opt-out of marketing communications</strong> we send you at any
      time. You can exercise this right by clicking on the “unsubscribe” or
      “opt-out” link in the marketing e-mails we send you or if you manage your
      preferences in the marketing preference centre in My Account.
    </li>
    <li>
      Similarly, if we have collected and process your personal information with
      your consent, for example consent to receive marketing from us or the Pets
      at Home Family, then you can <strong>withdraw your consent</strong> at any
      time. Withdrawing your consent will not affect the lawfulness of any
      processing we conducted prior to your withdrawal, nor will it affect
      processing of your personal information conducted in reliance on lawful
      processing grounds other than consent.
    </li>
    <li>
      You have the right to complain to a data protection authority about our
      collection and use of your personal information. For more information, see
      the section headed "<strong>Contacting the regulator</strong>"
      <a href="/privacy-policy#contacting-the-regulator">below</a>.
    </li>
  </ul>
  <p>
    We respond to all requests we receive from individuals wishing to exercise
    their data protection rights in accordance with applicable data protection
    laws.
  </p>

  <a id="contacting-the-regulator"></a>
  <h2>13. Contacting the regulator</h2>
  <p>
    If you feel that your personal information has not been handled correctly,
    or you are unhappy with our response to any requests you have made to us
    regarding the use of your personal information, you have the right to raise
    a complaint with the Information Commissioner's Office for UK residents, the
    Office of the Data Protection Authority for Guernsey residents, and Isle of
    Man Information Commissioner for Isle of Man residents. You can contact them
    via their websites, the details are below:
  </p>
  <ul>
    <li>
      <a href="https://ico.org.uk/" target="_blank" rel="noreferrer"
        >www.ICO.gov.uk</a
      >;
    </li>
    <li>
      <a href="https://www.odpa.gg/" target="_blank" rel="noreferrer"
        >www.odpa.gg</a
      >; and
    </li>
    <li>
      <a href="https://www.inforights.im/" target="_blank" rel="noreferrer"
        >www.inforights.IM</a
      >
    </li>
  </ul>

  <a id="policy-updates"></a>
  <h2>14. Updates to this Privacy Policy</h2>
  <p>
    We may update this Privacy Policy from time to time in response to changing
    legal, technical or business developments. When we update our Privacy
    Policy, we will take appropriate measures to inform you, consistent with the
    significance of the changes we make.
  </p>
  <p>
    You can see when this Privacy Policy was last updated by checking the “last
    updated” date displayed at the top of this Privacy Policy.
  </p>

  <a id="how-to-contact-us"></a>
  <h2>15. How to contact us</h2>
  <p>
    The data controller(s) of your personal information will be the Pets at Home
    Family member that you are dealing with, and as such could be Pets at Home
    Limited (including where you are a member of our Pets Club), Companion Care
    (Services) Limited, Vets4Pets Limited, Pets Insurance Services Limited
    (trading as Pets Insurance), Pets at Home Vet Group Limited, Vetsdirect
    Limited (trading as The Vet Connection), our affiliated charity, Pets
    Foundation or one of our affiliated joint venture first opinion veterinary
    practices branded either Companion Care, Vets for Pets or Vets4Pets.
  </p>
  <p>
    If you have any questions or concerns about the use of your personal
    information by the Pets at Home Family, please contact the Data Protection
    team at the relevant email address below depending on which Pets at Home
    Family member you are interacting with:
  </p>
  <ul>
    <li>
      Pets at Home, Vets for Pets, Vets4Pets, Companion Care, Pets Foundation:
      <a href="mailto:dataprotection@petsathome.co.uk"
        >dataprotection@petsathome.co.uk</a
      >
    </li>
    <li>
      The Vet Connection:
      <a href="mailto:info@thevetconnection.co.uk"
        >info@thevetconnection.co.uk</a
      >
    </li>
    <li>
      Pets Insurance:
      <a href="mailto:data-protection@petsinsurance.co.uk"
        >data-protection@petsinsurance.co.uk</a
      >
    </li>
  </ul>
</div>
